import { useQuery } from '@tanstack/react-query';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Box } from '@/components/Dropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import IdFilterDropdown from '@/pages/InvoiceList/components/IdFilterDropdown';
import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { api } from '@/utils/api';

type TeamLead = NonNullable<FreelancerInvoiceSubmissionResource['teamLead']>;

const StyledBox = styled(Box)`
  width: 100%;
  padding: 26px 24px 32px;
`;

const FilterHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

const FilterGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
`;

const FilterFooter = styled.div`
  margin-top: 24px;
  text-align: right;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

interface Props {
  selectedTeamLeads: number[];
  setSelectedTeamLeads: Dispatch<SetStateAction<number[]>>;
}

interface BoxProps extends Props {
  teamLeadOptions: { name: string; value: number }[];
  closeFilter: () => void;
}

const InvoiceApprovalsFilterBox: FC<BoxProps> = ({
  selectedTeamLeads,
  setSelectedTeamLeads,
  teamLeadOptions,
  closeFilter,
}) => {
  const [teamLeads, setTeamLeads] = useState(selectedTeamLeads);

  const apply = (ids: number[]) => {
    setSelectedTeamLeads(ids);
    closeFilter();
  };

  return (
    <StyledBox>
      <FilterHeading>
        <Text $type="button">Filter</Text>
        <BodyHeaderLine />
        <ResetButton onClick={() => apply([])}>
          <Text $color="error" $type="smallHeadline">
            Reset
          </Text>
        </ResetButton>
      </FilterHeading>
      <FilterGroup>
        <IdFilterDropdown
          filter={teamLeads}
          setFilter={setTeamLeads}
          options={teamLeadOptions}
          title="By Team Lead:"
        />
      </FilterGroup>
      <FilterFooter>
        <StyledButton onClick={closeFilter} color="secondary">
          Cancel
        </StyledButton>
        <ApplyButton color="colorized" onClick={() => apply(teamLeads)}>
          Apply filter
        </ApplyButton>
      </FilterFooter>
    </StyledBox>
  );
};

const InvoiceApprovalsFilter: FC<Props> = (props) => {
  const [showFilter, setShowFilter] = useState(false);

  // Loaded with the page, not the box, so applied tags always have names.
  const { data: teamLeadOptions = [] } = useQuery(
    ['freelancer_invoice_submissions_team_leads'],
    () =>
      api.get<never, TeamLead[]>(
        '/v1/payments/freelancer-invoice-submissions/team-leads',
      ),
    {
      staleTime: 60 * 1000,
      select: (leads) =>
        leads.map((lead) => ({ name: lead.name, value: lead.id })),
    },
  );

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowFilter(false));

  return (
    <div ref={ref}>
      <FilterButton
        onClick={() => setShowFilter(!showFilter)}
        isActive={showFilter}
        filterApplied={props.selectedTeamLeads.length > 0}
      />
      {showFilter && (
        <InvoiceApprovalsFilterBox
          {...props}
          teamLeadOptions={teamLeadOptions}
          closeFilter={() => setShowFilter(false)}
        />
      )}
    </div>
  );
};

export default InvoiceApprovalsFilter;
